import {TASK_RETENTION_IN_MONTHS} from '@nursio-link/common/utils';
import dayjs from 'dayjs';
import {CloudMoon, Sun, Sunrise, Sunset} from 'lucide-react';
import {sortObjectByKeys} from 'nx/src/utils/object-sort';
import {ascend, descend, groupBy, pathOr, pipe, sortWith} from 'ramda';
import type {ReactNode} from 'react';

import type {GetTasksWithDateRangeInput, Task} from '@/services/graphqlHooks';
import {PartOfTheDay} from '@/services/graphqlHooks';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {ForecastedTask} from './getForecastedTasks';

export type CombinedTask = Task & ForecastedTask;

export const groupByDayAndPartOfTheDay = (task: Task) => {
  const part = task.completeUntil?.partOfTheDay;

  return part ? part : 'none';
};

export const groupByDay = (task: Task) => task.date!;

export const sortAnnouncementTasks = (tasks: Task[]): Task[] => {
  return sortWith([descend(pathOr('', ['createdAt']))], tasks);
};

export const sortTasks = (tasks: Task[]): Task[] => {
  const groupedTasksPerDayMap = pipe(
    groupBy(groupByDay),
    sortObjectByKeys
  )(tasks);
  const days = Object.values(groupedTasksPerDayMap);

  const mappedDays = days.map(e =>
    pipe(groupBy(groupByDayAndPartOfTheDay), grouped => {
      return Object.keys(grouped).reduce(
        (acc, key) => {
          acc[key] = sortWith([
            ascend((task: Task) => (task.completeUntil?.time ? 0 : 1)),
            ascend(pathOr('', ['completeUntil', 'time'])),
            ascend(pathOr('', ['title'])),
          ])(grouped[key as PartOfTheDay] as Task[]);
          return acc;
        },
        {} as Record<string, Task[]>
      );
    })(e as Task[])
  );

  const filterTasksFrom5AMIsoOnward = (task: Task) => {
    if (task.completeUntil?.time) {
      return (
        new Date(`${task.date}T${task.completeUntil?.time}`).valueOf() <
        new Date(`${task.date}T05:00:00.000Z`).valueOf()
      );
    }
    return false;
  };

  const filterTasksTo5AMIso = (task: Task) => {
    if (task.completeUntil?.time) {
      return (
        new Date(`${task.date}T${task.completeUntil?.time}`).valueOf() >
        new Date(`${task.date}T05:00:00.000Z`).valueOf()
      );
    }
    return true;
  };

  const orderDayTasks = (dayTasks: Record<string, Task[]>): Task[] => [
    ...(dayTasks['none'] || []),
    ...(dayTasks[PartOfTheDay.Night] || []).filter(filterTasksFrom5AMIsoOnward),
    ...(dayTasks[PartOfTheDay.Morning] || []),
    ...(dayTasks[PartOfTheDay.Afternoon] || []),
    ...(dayTasks[PartOfTheDay.Evening] || []),
    ...(dayTasks[PartOfTheDay.Night] || []).filter(filterTasksTo5AMIso),
  ];

  return mappedDays.flatMap(orderDayTasks);
};

export const tasksWithDateRangeInput = (input?: GetTasksWithDateRangeInput) => {
  const startDate = dayjs(input?.startDate).isValid()
    ? input?.startDate
    : undefined;
  const endDate = dayjs(input?.endDate).isValid() ? input?.endDate : undefined;

  return {
    startDate: dayjs(startDate).format(TIME_FORMATS.awsDate),
    endDate: dayjs(endDate).format(TIME_FORMATS.awsDate),
  };
};

export const getPartOfTheDayIcon = (
  partOfTheDay: PartOfTheDay,
  size?: number
): ReactNode => {
  switch (partOfTheDay) {
    case PartOfTheDay.Morning:
      return <Sunrise size={size} />;
    case PartOfTheDay.Afternoon:
      return <Sun size={size} />;
    case PartOfTheDay.Evening:
      return <Sunset size={size} />;
    case PartOfTheDay.Night:
      return <CloudMoon size={size} />;
    default:
      return null;
  }
};

interface PartOfTheDayClasses {
  wrapper: string;
  indicator: string;
  icon: string;
  text: string;
}

export const getPartOfTheDayClasses = (
  partOfTheDay: PartOfTheDay
): PartOfTheDayClasses => {
  switch (partOfTheDay) {
    case PartOfTheDay.Morning:
      return {
        wrapper: 'border-blue-500',
        indicator: 'bg-blue-500',
        icon: 'text-blue-500',
        text: 'text-blue-700',
      };
    case PartOfTheDay.Afternoon:
      return {
        wrapper: 'border-yellow-500',
        indicator: 'bg-yellow-500',
        icon: 'text-yellow-500',
        text: 'text-yellow-600',
      };
    case PartOfTheDay.Evening:
      return {
        wrapper: 'border-orange-500',
        indicator: 'bg-orange-500',
        icon: 'text-orange-500',
        text: 'text-orange-700',
      };
    case PartOfTheDay.Night:
      return {
        wrapper: 'border-purple-500',
        indicator: 'bg-purple-500',
        icon: 'text-purple-500',
        text: 'text-purple-700',
      };
    default:
      return {
        wrapper: 'border-neutral',
        indicator: 'bg-neutral',
        icon: 'text-neutral-dark',
        text: 'text-neutral-dark',
      };
  }
};

export const getIsOverdue = (time?: string) => {
  if (!time) return;

  return dayjs.utc().isAfter(dayjs.utc(time));
};

export const isArchivedTaskPeriod = (endDate: string): boolean =>
  dayjs(endDate).isBefore(
    dayjs().subtract(TASK_RETENTION_IN_MONTHS, 'month'),
    'day'
  );

export const isStandardTask = (task: Task) =>
  Boolean(task?.completeUntil?.partOfTheDay || task?.completeUntil?.time);

export const splitTasksByType = (tasks: Task[]) => {
  return {
    announcementTasks: tasks.filter(task => !isStandardTask(task)),
    standardTasks: tasks.filter(isStandardTask),
  };
};
